/**
 * Empresas a que a pessoa pertence.
 *
 * O que está em memória mostra-se já, e pede-se outra vez por trás: criar
 * uma empresa ou aceitar um convite tem de aparecer sem recarregar a página.
 * Enquanto não há uma resposta boa, `temEmpresa` fica falso — na dúvida não
 * se mostra atalho nenhum, para um cliente curioso não cair no registo.
 * Um erro não conta como «não tem empresa»: guarda-se a última lista e
 * tenta-se de novo.
 */
import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  invalidarMinhasEmpresas,
  listaEmCache,
  pedirMinhasEmpresas,
  repetirMinhasEmpresas,
  subscreverMinhasEmpresas,
  versaoMinhasEmpresas,
} from './minhasEmpresasCache';

const VAZIO = [];

export { invalidarMinhasEmpresas };

export function useMinhasEmpresas() {
  const { user, isAuthenticated } = useAuth();
  const id = user?.id ?? null;
  const [versao, setVersao] = useState(versaoMinhasEmpresas);
  const [lista, setLista] = useState(() => (isAuthenticated ? listaEmCache(id) : null));
  const [falhou, setFalhou] = useState(false);

  useEffect(() => {
    const sair = subscreverMinhasEmpresas(() => setVersao(versaoMinhasEmpresas()));
    // A invalidação pode ter acontecido entre o estado inicial e esta
    // subscrição. Sem esta leitura, o ecrã ficava na versão antiga.
    setVersao(versaoMinhasEmpresas());
    return sair;
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !id) {
      setLista(null);
      setFalhou(false);
      return undefined;
    }

    const conhecida = listaEmCache(id);
    if (conhecida) setLista(conhecida);

    let vivo = true;
    pedirMinhasEmpresas(id).then((empresas) => {
      if (!vivo || versao !== versaoMinhasEmpresas()) return;
      setFalhou(false);
      setLista(empresas);
    }).catch(() => {
      if (!vivo) return;
      const ultima = listaEmCache(id);
      if (ultima) setLista(ultima);
      else setFalhou(true);
      repetirMinhasEmpresas();
    });

    return () => { vivo = false; };
  }, [isAuthenticated, id, versao]);

  const conhecido = Array.isArray(lista);
  const empresas = conhecido ? lista : VAZIO;
  return {
    empresas,
    temEmpresa: conhecido && empresas.length > 0,
    pronto: conhecido,
    falhou,
  };
}
